// src/pages/LoginPage.jsx
//
// Direction A — Two-column split.
// Left: black brand panel with cyan stripe, italic-gold headline.
// Right: cream panel with single Microsoft SSO button + reset link + IT footer.
import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, Typography, CircularProgress, useTheme } from "@mui/material";

import { loginRequest, b2cPolicies } from "../authConfig";
import { useAuth } from "../context/AuthContext";
import { SUPPORT_CONTACT } from "../config";
import AuthSplitLayout from "../components/auth/AuthSplitLayout";
import Eyebrow from "../components/brand/Eyebrow";
import { fonts } from "../theme/tokens";

const MicrosoftLogo = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <rect x="2" y="2" width="9" height="9" fill="#f25022" />
    <rect x="13" y="2" width="9" height="9" fill="#7fba00" />
    <rect x="2" y="13" width="9" height="9" fill="#00a4ef" />
    <rect x="13" y="13" width="9" height="9" fill="#ffb900" />
  </svg>
);

export default function LoginPage() {
  const theme = useTheme();
  const { instance, inProgress } = useMsal();
  const { user, initialized, noAccess, logoutLocal } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [redirecting, setRedirecting] = useState(false);
  const isNoAccessView = Boolean(location.state?.noAccess || noAccess);

  useEffect(() => {
    if (initialized && user && !isNoAccessView) {
      const redirectTo =
        sessionStorage.getItem("redirectAfterLogin") ||
        sessionStorage.getItem("lastVisited") ||
        "/dashboard";
      sessionStorage.removeItem("redirectAfterLogin");
      navigate(redirectTo, { replace: true });
    }
  }, [initialized, user, isNoAccessView, navigate]);

  const handleLogin = async () => {
    if (inProgress !== InteractionStatus.None) return;
    setRedirecting(true);
    try {
      await instance.loginRedirect(loginRequest);
    } catch (e) {
      const msg = e?.message || e?.errorMessage || "";
      if (msg.includes("AADB2C90118")) {
        await instance.loginRedirect({
          ...loginRequest,
          authority: b2cPolicies.authorities.resetPassword.authority,
        });
        return;
      }
      console.error("LoginRedirect error:", e);
      setRedirecting(false);
    }
  };

  const isAuthenticating =
    inProgress !== InteractionStatus.None || !initialized || redirecting;

  return (
    <AuthSplitLayout
      leftEyebrow="Property management portal"
      leftTitle={
        <>
          Manage every<br />
          block{" "}
          <Box
            component="em"
            sx={{ fontStyle: "italic", color: theme.palette.brand.gold }}
          >
            brilliantly
          </Box>
          .
        </>
      }
      leftSubtitle="The internal workspace for XL@Property — block management across Kent and the South-East."
    >
      {isNoAccessView ? (
        <NoAccessView onSignOut={() => logoutLocal("Signed out.")} />
      ) : (
        <>
          <Eyebrow sx={{ color: theme.palette.brand.goldDeep, mb: 1.5 }}>
            Welcome back
          </Eyebrow>
          <Typography
            sx={{
              fontFamily: fonts.serif,
              fontSize: 38,
              fontWeight: 500,
              lineHeight: 1.1,
              m: 0,
              mb: 1,
              color: theme.palette.brand.ink,
            }}
          >
            Sign in
          </Typography>
          <Typography sx={{ fontSize: 14, color: theme.palette.brand.inkMuted, mb: 4 }}>
            Use your XL@Property work account.
          </Typography>

          <Button
            fullWidth
            disableElevation
            onClick={handleLogin}
            disabled={isAuthenticating}
            sx={{
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.brand.ink,
              border: `1px solid ${theme.palette.brand.borderStrong}`,
              borderRadius: "6px",
              py: 1.6,
              fontWeight: 600,
              fontSize: 14,
              gap: 1.25,
              "&:hover": {
                backgroundColor: theme.palette.brand.surfaceMuted,
                borderColor: theme.palette.brand.ink,
              },
              "&.Mui-disabled": {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.brand.inkMuted,
              },
            }}
          >
            {isAuthenticating ? (
              <>
                <CircularProgress size={16} sx={{ color: theme.palette.brand.inkMuted }} />
                Redirecting to Microsoft…
              </>
            ) : (
              <>
                <MicrosoftLogo />
                Continue with Microsoft
              </>
            )}
          </Button>

          <Box
            sx={{
              mt: 4,
              pt: 2.5,
              borderTop: `1px solid ${theme.palette.brand.border}`,
              fontSize: 12.5,
              color: theme.palette.brand.inkSoft,
              lineHeight: 1.55,
              textAlign: "center",
            }}
          >
            Locked out or new manager? Email{" "}
            <Box component="span" sx={{ color: theme.palette.brand.ink, fontWeight: 600 }}>
              {SUPPORT_CONTACT.email}
            </Box>
            <br />
            or call{" "}
            <Box component="span" sx={{ color: theme.palette.brand.ink, fontWeight: 600 }}>
              {SUPPORT_CONTACT.phone}
            </Box>
          </Box>
        </>
      )}
    </AuthSplitLayout>
  );
}

function NoAccessView({ onSignOut }) {
  const theme = useTheme();
  return (
    <>
      <Eyebrow sx={{ color: theme.palette.brand.goldDeep, mb: 1.5 }}>
        Account verified
      </Eyebrow>
      <Typography
        sx={{
          fontFamily: fonts.serif,
          fontSize: 32,
          fontWeight: 500,
          lineHeight: 1.15,
          m: 0,
          mb: 1.5,
        }}
      >
        Awaiting access
      </Typography>
      <Typography sx={{ fontSize: 14, color: theme.palette.brand.inkMuted, mb: 3.5 }}>
        Your account is authenticated but has no assigned access yet. Ask a
        director or the IT team to grant access to the portal.
      </Typography>
      <Button
        fullWidth
        variant="outlined"
        onClick={onSignOut}
        sx={{
          py: 1.4,
          fontWeight: 600,
          color: theme.palette.brand.ink,
          borderColor: theme.palette.brand.borderStrong,
          "&:hover": {
            backgroundColor: theme.palette.brand.surfaceMuted,
            borderColor: theme.palette.brand.ink,
          },
        }}
      >
        Sign out
      </Button>
    </>
  );
}
